import moment from 'moment-timezone';
import { formatDateWithYear } from '@/utils/date';
import type {
  CostInfo,
  PurchaseHistoryEntry,
  SaltInfo,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import type {
  GetOrderEntryItemDetailQuery,
  ItemDetailContent_SaltInfoFieldsFragment,
} from '@/types';

type GqlPurchaseHistoryEntry =
  GetOrderEntryItemDetailQuery['getOrderEntryItemDetail']['purchase_history'][number];

type GqlCostInfo = NonNullable<GqlPurchaseHistoryEntry['cost_info']>;

/**
 * Maps the raw SALT fragment to the camelCased {@link SaltInfo} view model.
 *
 * @param source - The SALT info fragment, or null/undefined when absent.
 * @returns The mapped {@link SaltInfo}, or null when there is no source.
 */
export const mapSaltInfo = (
  source: ItemDetailContent_SaltInfoFieldsFragment | null | undefined,
): SaltInfo | null => {
  if (!source) {
    return null;
  }
  return {
    additionalInformation: source.additional_information,
    description: source.description ?? null,
    imageUrls: source.image_urls ?? null,
    longDescription: source.long_description ?? null,
    name: source.name ?? null,
    packaging: source.packaging,
  };
};

const mapCostInfo = (
  source: GqlCostInfo | null | undefined,
): CostInfo | null => {
  if (!source?.details) {
    return null;
  }
  return {
    details: source.details.map((detail) => ({
      costInMicros: detail.cost_in_micros,
      type: detail.type,
    })),
  };
};

/**
 * Maps a raw purchase-history entry to the camelCased
 * {@link PurchaseHistoryEntry} view model, pre-formatting the fulfillment date
 * in the restaurant's timezone.
 *
 * @param entry - The raw purchase-history entry from the item-detail query.
 * @param restaurantTimezone - IANA timezone used to render the fulfillment date.
 * @returns The mapped {@link PurchaseHistoryEntry}.
 */
export const mapPurchaseHistoryEntry = (
  entry: GqlPurchaseHistoryEntry,
  restaurantTimezone: string,
): PurchaseHistoryEntry => ({
  baseQuantity: entry.base_quantity ?? null,
  baseUnit: entry.base_unit ?? null,
  baseUnitPriceInMicros: entry.base_unit_price_in_micros ?? null,
  costInfo: mapCostInfo(entry.cost_info),
  currencyCode: entry.currency_code,
  fulfillmentDate: entry.fulfillment_date ?? null,
  fulfillmentDateDisplay: entry.fulfillment_date
    ? formatDateWithYear(moment.tz(entry.fulfillment_date, restaurantTimezone))
    : '',
  itemModifiers: entry.item_modifiers ?? null,
  orderDate: entry.order_date ?? null,
  orderId: entry.order_id ?? null,
  orderItemId: entry.order_item_id ?? null,
  priceInMicros: entry.price_in_micros ?? null,
  quantity: entry.quantity ?? null,
  salesRepGrossProfitInMicros: entry.sales_rep_gross_profit_in_micros ?? null,
  salesRepGrossProfitInPercentage:
    entry.sales_rep_gross_profit_in_percentage ?? null,
  totalPriceInMicros: entry.total_price_in_micros ?? null,
  unit: entry.unit ?? null,
  unitCount: entry.unit_count ?? null,
});
